"use client";

import { useEffect, useState, useMemo } from "react";
import { getLenis } from "@/components/SmoothScrollProvider";
import { cn } from "@/utils/cn";
import { usePathname, useRouter, useParams } from "next/navigation";
import RegisterModal from "@/components/RegisterModal";
import { useFullPageContext } from "@/context/FullPageContext";
import StartJourneyButton from "@/components/Buttons/StartJourneyButton";

const SECTIONS = [
  { id: "01", label: "Câu chuyện", href: "#cau-chuyen-thuong-hieu" },
  { id: "02", label: "Tổng quan", href: "#tong-quan-du-an" },
  { id: "03", label: "Vị trí", href: "#vi-tri-ket-noi" },
  { id: "04", label: "Trải nghiệm", href: "#mot-ngay-song" },
  { id: "05", label: "Không gian", href: "#khong-gian-song" },
  { id: "06", label: "Giải pháp an cư", href: "#giai-phap-an-cu" },
  { id: "07", label: "Tin tức", href: "#tin-tuc" },
  { id: "08", label: "Liên hệ", href: "#dang-ky-bao-gia" },
];

export default function ScrollHeader({
  visibleSections,
  apptOptions,
  formTitle,
  formSubtitle,
  formLayoutTypeTitle,
  logoUrl,
}: {
  alwaysVisible?: boolean;
  visibleSections?: Record<string, boolean>;
  apptOptions?: { label: string; value: string }[];
  formTitle?: string;
  formSubtitle?: string;
  formLayoutTypeTitle?: string;
  logoUrl?: string;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const locale = params?.locale || "vi";

  const {
    activeIndex,
    scrollToSection,
    isFullPage,
    sections: fullPageSections,
  } = useFullPageContext();

  // Check if we are on the homepage
  const isHome = pathname === "/" || pathname === `/${locale}`;

  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpenMobile, setIsOpenMobile] = useState(false);
  const [activeSection, setActiveSection] = useState("#banner-anchor");
  const [openRegister, setOpenRegister] = useState(false);

  // Dynamic header background based on active section transparent prop
  const currentFullPageSec =
    isFullPage && fullPageSections.length > 0
      ? fullPageSections[activeIndex]
      : null;

  const isTransparent = isHome
    ? isFullPage
      ? Boolean(currentFullPageSec?.transparent)
      : !isScrolled || activeSection === "#banner-anchor"
    : false;

  const activeSections = useMemo(() => {
    if (!isHome) {
      return [
        { id: "01", label: "Trang chủ", href: "/" },
        { id: "05", label: "Tin tức", href: "/tin-tuc" },
      ];
    }
    return SECTIONS.filter(
      (s) => !visibleSections || visibleSections[s.href] !== false,
    );
  }, [isHome, visibleSections]);

  const currentActive = useMemo(() => {
    if (!isHome) {
      if (pathname.includes("/tin-tuc")) {
        return "/tin-tuc";
      }
      return "/";
    }
    if (activeSection.startsWith("#story-screen-")) {
      return "#cau-chuyen-thuong-hieu";
    }
    return activeSection;
  }, [isHome, pathname, activeSection]);

  // Sync active section in FullPage mode
  useEffect(() => {
    if (isFullPage && fullPageSections.length > 0) {
      const currentSec = fullPageSections[activeIndex];
      if (currentSec) {
        setActiveSection(`#${currentSec.id}`);
      }
    }
  }, [isFullPage, activeIndex, fullPageSections]);

  // Track scroll positions for active sections in non-FullPage mode
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      if (!isHome || isFullPage) return;

      const scrollPosition = window.scrollY + 120;

      if (window.scrollY < 200) {
        setActiveSection("#banner-anchor");
        return;
      }

      for (const item of activeSections) {
        if (item.href === "#banner-anchor") continue;
        const el = document.querySelector(item.href);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY;
          const height = el.clientHeight;
          if (
            scrollPosition >= top - 20 &&
            scrollPosition < top + height - 20
          ) {
            setActiveSection(item.href);
            return;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome, isFullPage, activeSections]);

  const handleMenuClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setIsOpenMobile(false);

    if (isHome) {
      if (isFullPage && fullPageSections.length > 0) {
        const targetId = href.replace("#", "");
        let targetIdx = fullPageSections.findIndex((s) => s.id === targetId);

        // Alias fallback search for story section screens & alternative section IDs
        if (targetIdx === -1) {
          if (
            targetId === "cau-chuyen-thuong-hieu" ||
            targetId === "cau-chuyen"
          ) {
            targetIdx = fullPageSections.findIndex((s) =>
              s.id.startsWith("story-screen-"),
            );
          } else if (targetId === "tong-quan") {
            targetIdx = fullPageSections.findIndex(
              (s) => s.id === "tong-quan-du-an",
            );
          } else if (targetId === "vi-tri") {
            targetIdx = fullPageSections.findIndex(
              (s) => s.id === "vi-tri-ket-noi",
            );
          } else if (targetId === "trai-nghiem") {
            targetIdx = fullPageSections.findIndex(
              (s) => s.id === "mot-ngay-song",
            );
          } else if (targetId === "khong-gian") {
            targetIdx = fullPageSections.findIndex(
              (s) => s.id === "khong-gian-song",
            );
          } else if (targetId === "hanh-trinh") {
            targetIdx = fullPageSections.findIndex(
              (s) => s.id === "tien-do-du-an",
            );
          } else if (
            targetId === "dang-ky-bao-gia" ||
            targetId === "lien-he" ||
            targetId === "footer"
          ) {
            targetIdx = fullPageSections.findIndex(
              (s) => s.id === "dang-ky-bao-gia" || s.id === "footer",
            );
          } else if (targetId === "thu-vien") {
            targetIdx = fullPageSections.findIndex((s) => s.id === "thu-vien");
          } else if (targetId === "tin-tuc" || targetId === "news") {
            targetIdx = fullPageSections.findIndex((s) => s.id === "tin-tuc");
          }
        }

        if (targetIdx !== -1) {
          scrollToSection(targetIdx);
          return;
        }
      }
      const lenis = getLenis();
      const el = document.querySelector(href);
      if (el) {
        if (lenis) {
          lenis.scrollTo(href, { offset: -72 });
        } else {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      } else {
        const formEl = document.getElementById("dang-ky-bao-gia");
        if (formEl) {
          if (lenis) {
            lenis.scrollTo("#dang-ky-bao-gia", { offset: -72 });
          } else {
            formEl.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }
      }
    } else {
      if (href === "/") {
        router.push(`/${locale}`);
      } else if (href === "/tin-tuc") {
        router.push(`/${locale}/tin-tuc`);
      } else {
        router.push(`/${locale}${href}`);
      }
    }
  };

  const handleStartJourney = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isFullPage && fullPageSections.length > 0) {
      const nextIdx = Math.min(activeIndex + 1, fullPageSections.length - 1);
      scrollToSection(nextIdx);
    } else {
      const nextSec = fullPageSections[activeIndex + 1];
      if (nextSec) {
        const el = document.getElementById(nextSec.id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        const formEl = document.getElementById("dang-ky-bao-gia");
        if (formEl) {
          formEl.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  const handleLogoClick = () => {
    if (isHome) {
      if (isFullPage && fullPageSections.length > 0) {
        scrollToSection(0);
        return;
      }
      const lenis = getLenis();
      if (lenis) {
        lenis.scrollTo("#banner-anchor");
      } else {
        const el = document.getElementById("banner-anchor");
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    } else {
      router.push(`/${locale}`);
    }
  };

  return (
    <>
      {/* HEADER: FIXED HEIGHT (h-16 md:h-20), ALWAYS VERTICALLY CENTERED */}
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-header h-16 md:h-18 transition-all duration-300 w-full flex items-center",
          isTransparent
            ? "bg-transparent border-transparent"
            : "bg-[#0E1720]/15 border-b border-white/20 backdrop-blur-md ",
        )}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full h-full flex items-center justify-between relative">
          {/* LEFT: LOGO */}
          <div
            onClick={handleLogoClick}
            className="flex items-center cursor-pointer transition hover:opacity-90 flex-shrink-0"
          >
            <img
              src={logoUrl || "/logos/logo.png"}
              alt="Dragon Home"
              className="h-7 md:h-8 w-auto object-contain"
            />
          </div>

          {/* CENTER: NAV MENU */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
            {activeSections.map((section) => {
              const isActive = currentActive === section.href;
              return (
                <a
                  key={section.id}
                  href={section.href}
                  onClick={(e) => handleMenuClick(e, section.href)}
                  className={cn(
                    "text-[11px] xl:text-xs font-normal transition-all duration-300 relative py-1",
                    isActive
                      ? "text-white font-medium after:content-[''] after:absolute after:-bottom-2 after:left-0 after:right-0 after:h-[1.5px] after:bg-[#C9AB81]"
                      : "text-white hover:text-white",
                  )}
                >
                  {section.label}
                </a>
              );
            })}
          </nav>

          {/* RIGHT ACTION BUTTON: BẮT ĐẦU HÀNH TRÌNH (Desktop) */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-5 flex-shrink-0">
            <StartJourneyButton onClick={handleStartJourney} />
          </div>

          {/* MOBILE CONTROLS & HAMBURGER */}
          <div className="lg:hidden flex items-center gap-3 ml-auto">
            <StartJourneyButton
              onClick={handleStartJourney}
              className="px-3 py-1 text-[10px]"
            />

            <button
              type="button"
              onClick={() => setIsOpenMobile(true)}
              className="p-1.5 text-white hover:text-[#C9AB81] transition-colors"
              aria-label="Toggle Menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      {isOpenMobile && (
        <div className="fixed inset-0 z-above_header bg-black/70 backdrop-blur-sm lg:hidden">
          <div className="absolute right-0 top-0 h-full w-[290px] bg-[#0B131D] p-6 text-white shadow-2xl flex flex-col gap-6 animate-slide-in border-l border-white/10">
            <div className="flex justify-between items-center border-b border-white/10 pb-4">
              <img
                src={logoUrl || "/logos/logo.png"}
                alt="Logo"
                className="h-7 object-contain"
              />
              <button
                type="button"
                onClick={() => setIsOpenMobile(false)}
                className="text-white text-xl p-2 hover:opacity-80 transition"
              >
                ✕
              </button>
            </div>

            <nav className="flex flex-col gap-3 overflow-y-auto max-h-[60vh] pr-2">
              {activeSections.map((item) => {
                const isActive = currentActive === item.href;
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleMenuClick(e, item.href)}
                    className={cn(
                      "text-xs py-2.5 border-b border-white/5 font-normal transition duration-200",
                      isActive
                        ? "text-[#C9AB81] font-medium"
                        : "text-white/70 hover:text-white",
                    )}
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>

            <div className="mt-auto flex flex-col gap-3 border-t border-white/10 pt-6">
              <StartJourneyButton
                onClick={(e) => {
                  setIsOpenMobile(false);
                  handleStartJourney(e);
                }}
                className="w-full justify-center py-2.5"
              />
            </div>
          </div>
        </div>
      )}

      <RegisterModal
        open={openRegister}
        onClose={() => setOpenRegister(false)}
        apptOptions={apptOptions}
        formTitle={formTitle}
        formSubtitle={formSubtitle}
        formLayoutTypeTitle={formLayoutTypeTitle}
      />
    </>
  );
}
